// Growth Roadmap — one page, two ladders. Which one a person sees is decided
// by their department's family (Sales/BA vs Engineering); which rungs show
// full detail vs stay locked is decided by employment type and a best-effort
// match against their position title. Source for the Sales ladder: the real,
// live "THE LADDER (L0 -> L7)" operating doc (sales-revops Docs, 2026-08-14) —
// transcribed verbatim, not paraphrased, because this is the actual spine the
// sales org is run against (market band, ACV, proof gates), not a mockup.
// The Engineering ladder is Bassi's own dictated rung list. A skill-level
// breakdown per rung now exists at Docs/Company/EngineeringSkillUniverse.md,
// but it's a first draft pending his calibration (rungs 3-7 have no legacy
// precedent), so each rung here still carries a short, honest description
// instead of invented numbers.

export const SALES_LADDER = {
  key: "sales",
  title: "Sales Growth Ladder",
  subtitle: "L0 -> L7 — the rung is a market you can carry, not a title. Every rung does the same work, re-cut deeper.",
  law: [
    "The rung is a MARKET, not a title — you're L4 because you can hold a 200-1000 employee buying committee, not because of what payroll calls you.",
    "Nothing is ever left behind. L7 still does L1 work, just at a depth an L1 can't see.",
    "Promotion is proof, never tenure — two independent closes at the target band, unassisted on the load-bearing move.",
    "Reality beats report — a rung is set by call recordings and closed-won evidence, not by forecast confidence.",
  ],
  rungs: [
    {
      level: "L0",
      title: "Extreme Junior / Trainee",
      band: "None — internal only",
      buyer: "—",
      acv: "—",
      cycle: "—",
      ratio: "90% research : 0% talk : 10% orchestrate",
      shift: "From day one to making the first stranger respond.",
      proofGate: null,
      matchKeywords: ["trainee"],
    },
    {
      level: "L1",
      title: "Junior SDR",
      band: "Micro (under 50 employees)",
      buyer: "Founder / owner",
      acv: "₹50k – ₹3L",
      cycle: "3–14 days",
      ratio: "60% research : 30% talk : 10% orchestrate",
      shift: "From learning the product to making the first stranger respond.",
      proofGate: "100 personalised touches, ≥8% reply rate, 5 qualified meetings booked, product quiz 100%, 1 clean call recording.",
      matchKeywords: ["junior sdr", "junior gtm sales", "sdr"],
    },
    {
      level: "L2",
      title: "SDR → Junior AE",
      band: "Small (50–150 employees)",
      buyer: "Founder + 1 functional head",
      acv: "₹3L – ₹8L",
      cycle: "2–5 weeks",
      ratio: "45% research : 40% talk : 15% orchestrate",
      shift: "From a response to running a full conversation without a senior in the room.",
      proofGate: "3 closed-won in the Micro band, unassisted discovery, objection library recall live with no notes.",
      matchKeywords: ["junior ae"],
    },
    {
      level: "L3",
      title: "AE (Small-Mid)",
      band: "Small-Mid (150–500 employees)",
      buyer: "Head of HR / Ops / Eng",
      acv: "₹8L – ₹20L",
      cycle: "4–8 weeks",
      ratio: "40% research : 40% talk : 20% orchestrate",
      shift: "From selling to a person to selling to a function that has a budget and a boss.",
      proofGate: "2 closed-won in Small-Mid, each with a written cost-of-inaction model the buyer confirmed in writing.",
      wall: {
        name: "The Quantification Wall",
        symptom: "“They loved it but went quiet.” The rep can talk but can't do arithmetic in front of a buyer.",
        fix: "Live cost-of-inaction math on every call, no exceptions.",
      },
      matchKeywords: ["account executive", "ae"],
    },
    {
      level: "L4",
      title: "Mid-Market AE",
      band: "Mid (500–1500 employees)",
      buyer: "Director + Finance + IT touch",
      acv: "₹20L – ₹60L",
      cycle: "6–14 weeks",
      ratio: "35% research : 35% talk : 30% orchestrate",
      shift: "From one champion to a committee where the buyer and the payer are different people.",
      proofGate: "2 closed-won in Mid with ≥4 stakeholders touched, 1 deal saved after champion loss, forecast accuracy within 15% for 2 quarters.",
      wall: {
        name: "The Multithread Wall",
        symptom: "The deal dies when the one champion goes on leave or switches jobs.",
        fix: "Mandatory 3-contact rule before Stage 3.",
      },
      matchKeywords: ["mid-market ae", "mid market ae"],
    },
    {
      level: "L5",
      title: "Senior AE (Mid-Large)",
      band: "Mid-Large (1500–5000 employees)",
      buyer: "VP + procurement + InfoSec",
      acv: "₹60L – ₹2Cr",
      cycle: "3–6 months",
      ratio: "30% research : 30% talk : 40% orchestrate",
      shift: "From convincing to surviving procurement, security and legal without losing the price.",
      proofGate: "2 closed-won in Mid-Large that cleared procurement + security review, ≤10% discount from list, MSA redlines handled without the founder.",
      matchKeywords: ["senior ae"],
    },
    {
      level: "L6",
      title: "Strategic AE (Large)",
      band: "Large (5000–20000 employees)",
      buyer: "CXO-1, multi-BU",
      acv: "₹2Cr – ₹6Cr",
      cycle: "6–12 months",
      ratio: "30% research : 25% talk : 45% orchestrate",
      shift: "From a deal to a program — multi-BU, multi-year, internal politics as terrain.",
      proofGate: "1 multi-BU program won, 1 competitive displacement, an exec-to-exec meeting run by the rep with the founder present but silent.",
      wall: {
        name: "The Politics Wall",
        symptom: "“We were technically selected but they went with the incumbent.” The rep believes the best solution wins — at Large, the safest sponsor wins.",
        fix: "Map who gets promoted if this works and who gets blamed if it fails, before the pricing conversation.",
      },
      matchKeywords: ["strategic ae"],
    },
    {
      level: "L7",
      title: "Enterprise / Key Accounts",
      band: "Enterprise (20000+ / listed / global)",
      buyer: "CXO + board-adjacent",
      acv: "₹6Cr+ / multi-year",
      cycle: "9–24 months",
      ratio: "25% research : 20% talk : 55% orchestrate",
      shift: "From a program to a partnership — you're now part of their capital allocation plan.",
      proofGate: "1 enterprise account landed and expanded 2x or more within 12 months, named as a reference account, one internal operator trained to L4 by them.",
      wall: {
        name: "The Patience Wall",
        symptom: "The rep needs the quarter; the account needs the year — discounting to force a close torches the multi-year frame.",
        fix: "Comp and forecast on landing + expansion arc, not signature date.",
      },
      matchKeywords: ["enterprise ae", "key account"],
    },
  ],
};

export const ENGINEERING_LADDER = {
  key: "engineering",
  title: "Engineering Growth Ladder",
  subtitle: "Early draft — the rungs are confirmed; the proof gates for each one are still being written.",
  law: [
    "Every rung still ships code. A senior rung means a bigger, fuzzier problem, not less building.",
    "AI tools are a multiplier at every rung, from the first intern task to a release manager's review.",
    "The ladder forks after FDE/Release Manager — customer-facing delivery and product ownership are two different jobs, not a ranking of one above the other.",
  ],
  rungs: [
    {
      level: "1",
      title: "Intern",
      shift: "Learning the stack and the codebase, shipping small tasks with AI tools as a multiplier, under close review.",
      proofGate: null,
      matchKeywords: ["intern"],
    },
    {
      level: "2",
      title: "Independent AI-aided Full Stack Dev",
      shift: "Owns a feature end to end without a senior pairing on every PR — the default rung for a confirmed Full Stack engineer.",
      proofGate: null,
      matchKeywords: ["full stack", "ai aided full stack", "full-stack"],
    },
    {
      level: "3",
      title: "Core Applied AI/ML Engineer",
      shift: "Builds and ships the AI/ML pieces themselves, not just the surface around someone else's model.",
      proofGate: null,
      matchKeywords: ["applied ai", "ml engineer", "ai/ml", "machine learning"],
    },
    {
      level: "4",
      title: "FDE / Release Manager",
      shift: "Owns a release end to end — the checkpoints, the risk, what ships and what waits.",
      proofGate: null,
      matchKeywords: ["release manager", "fde", "forward deployed"],
    },
  ],
  fork: {
    label: "The ladder splits here — pick the track, or do both.",
    branches: [
      {
        level: "5a",
        title: "Full Timer — customer-facing FDE",
        shift: "Sits in front of the customer directly, owns delivery as a relationship, not just a repo.",
        proofGate: null,
        matchKeywords: ["customer-facing", "customer facing fde"],
      },
      {
        level: "5b",
        title: "Product Owner track",
        shift: "Owns what gets built and why, not just how — the product-decisions side of delivery.",
        proofGate: null,
        matchKeywords: ["product owner"],
      },
    ],
  },
  rungsAfterFork: [
    {
      level: "6",
      title: "Sales meeting / customer owner",
      shift: "Runs the customer conversation directly — sits in the room where the next piece of work gets decided.",
      proofGate: null,
      matchKeywords: ["customer owner", "account owner"],
    },
    {
      level: "7",
      title: "Revenue Driver",
      shift: "What you build and own measurably moves the top line, not just the delivery metrics.",
      proofGate: null,
      matchKeywords: ["revenue driver"],
    },
  ],
};

// The engineering ladder forks mid-way, so anything that needs one flat,
// matchable sequence (current-rung matching, a compact "where is this person
// today" label) reads from here rather than re-flattening rungs/fork/after
// in every consumer.
export const ENGINEERING_FLAT = [
  ...ENGINEERING_LADDER.rungs,
  ...ENGINEERING_LADDER.fork.branches,
  ...ENGINEERING_LADDER.rungsAfterFork,
];

// A keyword matches only as a whole word (or phrase), so "intern" does not
// fire inside "Internal Tools Engineer" or "International Sales". A plural
// "s" is allowed ("Interns", "SDRs"). Both callers below use this one check.
function titleHasKeyword(needle, keyword) {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z0-9])${escaped}s?($|[^a-z0-9])`).test(needle);
}

// Length of the longest keyword of this rung found in the title, or 0.
function rungMatchLength(rung, needle) {
  return (rung.matchKeywords || []).reduce(
    (best, kw) => (kw.length > best && titleHasKeyword(needle, kw) ? kw.length : best),
    0,
  );
}

// Best-effort "where are you on this ladder" — matched against position
// title only, since no real activity data (calls, PR/release history) feeds
// this yet. Deliberately conservative: falls back to the first rung rather
// than guessing wrong in a direction that looks like inflation. When several
// rungs match, the most specific (longest) keyword wins, so "Junior AE" lands
// on the Junior AE rung and not on the generic "AE" rung above it; on a tie
// the higher rung wins.
export function matchCurrentRungIndex(flatRungs, positionTitle) {
  const needle = (positionTitle || "").toLowerCase();
  if (!needle) return 0;
  let bestIdx = 0;
  let bestLen = 0;
  for (let i = 0; i < flatRungs.length; i += 1) {
    const len = rungMatchLength(flatRungs[i], needle);
    if (len > 0 && len >= bestLen) {
      bestIdx = i;
      bestLen = len;
    }
  }
  return bestIdx;
}

// A manager/HR-set rung always wins over the position-title guess above —
// it's a person who actually knows, not a keyword match against a job
// title. Falls through to the guess when nothing's been set, or when a set
// value doesn't match any rung on THIS ladder (e.g. stale after a ladder
// edit), rather than erroring.
export function resolveRungIndex(flatRungs, positionTitle, overrideLevel) {
  if (overrideLevel) {
    const idx = flatRungs.findIndex((r) => r.level === overrideLevel);
    if (idx !== -1) return idx;
  }
  return matchCurrentRungIndex(flatRungs, positionTitle);
}

// Which ladder a department_family sees. Shared so every caller (Growth
// Roadmap, HRMS, the home page) resolves the same way instead of each
// re-writing this same three-way branch.
export function ladderFor(family) {
  return family === "Sales/BA" ? SALES_LADDER : family === "Engineering" ? ENGINEERING_LADDER : null;
}

// Compact "where someone is today" label, e.g. "L3 · AE (Small-Mid)" --
// null when the family has no ladder at all.
export function growthRungLabel(family, positionTitle, overrideLevel) {
  const ladder = ladderFor(family);
  if (!ladder) return null;
  const flat = ladder.key === "sales" ? ladder.rungs : ENGINEERING_FLAT;
  const idx = resolveRungIndex(flat, positionTitle, overrideLevel);
  const rung = flat[idx];
  return rung ? `${rung.level} · ${rung.title}` : null;
}

// True only when there's a REAL signal to place someone on the ladder -- a
// manager/HR-set override, or a position title that actually matches a
// rung's own wording. matchCurrentRungIndex silently returns index 0 both
// for a genuine L0 match ("trainee") and for a title that matched nothing
// at all (e.g. "Manager", "Developer") -- those two cases are NOT the same
// thing, and a caller that shows a rung label whenever one comes back would
// show "Extreme Junior / Trainee" next to someone whose title just didn't
// happen to contain any of this ladder's keywords. Callers that display the
// rung to the person themselves (rather than an HR admin screen, where
// seeing "needs classification" is the useful signal) should gate on this
// first.
export function hasConfidentRung(family, positionTitle, overrideLevel) {
  const ladder = ladderFor(family);
  if (!ladder) return false;
  if (overrideLevel) return true;
  const needle = (positionTitle || "").toLowerCase();
  if (!needle) return false;
  const flat = ladder.key === "sales" ? ladder.rungs : ENGINEERING_FLAT;
  return flat.some((rung) => rungMatchLength(rung, needle) > 0);
}
